import axios from "axios";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import { API_ROUTES, ROUTES_URL } from "@/constant";
import { LoginCustomerData } from "@/types/authService";

type Currency = "S$" | "RM" | null;

interface AuthState {
  token: string | null;
  refresh_token: string | null;
  hashKey: string | null;
  email: string | null;
  isAuthenticated: boolean;
  isTCAccepted: boolean;
  isMalaysia: boolean;
  isSingapore: boolean;
  customer: LoginCustomerData | null;
  login: (
    token: string,
    refresh_token: string,
    customer: LoginCustomerData,
    isTCAccepted: boolean,
  ) => void;
  logout: () => void;
  updateState: (partial: Partial<AuthState>) => void;
  currency: Currency;
  refreshTokenIfNeeded: () => Promise<string>;
}

let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = async (payload: any): Promise<any> => {
  const response = await axios.post(
    `${process.env.AUTH_API_BASE_URL}/authentication${API_ROUTES.UPDATE_ACCESS_TOKEN}`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.data;
};

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        token: null,
        refresh_token: null,
        email: null,
        hashKey: null,
        isAuthenticated: false,
        isTCAccepted: true,
        customer: null,
        isMalaysia: false,
        isSingapore: false,
        currency: null,
        login: (token, refresh_token, customer, isTCAccepted) =>
          set(() => ({
            token,
            refresh_token,
            isAuthenticated: true,
            customer,
          })),
        logout: () =>
          set(() => ({
            token: null,
            refresh_token: null,
            email: null,
            hashKey: null,
            isAuthenticated: false,
            isTCAccepted: true,
            customer: null,
            isMalaysia: false,
            isSingapore: false,
            currency: null,
          })),
        updateState: (partial) =>
          set((state) => ({
            ...state,
            ...partial,
          })),

        refreshTokenIfNeeded: async () => {
          if (refreshPromise) {
            return refreshPromise;
          }

          refreshPromise = (async () => {
            try {
              const { refresh_token } = get();

              if (!refresh_token) {
                throw new Error("No refresh token found");
              }

              const response = await refreshAccessToken({
                refresh_token,
              });

              const newAccessToken = response.data.token;

              set({
                token: newAccessToken,
              });

              return newAccessToken;
            } catch (error) {
              get().logout();

              window.location.href = ROUTES_URL.LOGIN;

              throw error;
            }
          })();

          try {
            return await refreshPromise;
          } finally {
            refreshPromise = null;
          }
        },
      }),
      {
        name: "auth-storage",
      },
    ),
    {
      name: "Auth Store",
    },
  ),
);

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === "auth-storage") {
      const newValue = event.newValue ? JSON.parse(event.newValue) : null;
      if (newValue?.state) {
        useAuthStore.setState(newValue.state);
      }
    }
  });
}
